import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate L2-5 del Warlock (manuale V1, rr. 5987-6097).
 *
 * 16 mosse di tipo `advanced_2_5`. Pattern stress-testati:
 *
 * - **`prerequisites.anyOf` cross-class verso Paladino**: 8 mosse hanno
 *   «Richiede: *Contratto* o *Ordine Sovrannaturale*» (manuale rr. 5993, 5999, 6149, ecc.).
 *   `anyOf: ['warlock.contratto', 'paladino.ordine-sovrannaturale']`.
 *
 * - **`prerequisites.anyOf` cross-class verso Guerriero** (D7 cross-class): *L'Anima
 *   dell'Acciaio* e *Lama Ultraterrena* hanno «Richiede: *Vincolo della Lama* o *Arma
 *   Prescelta*» — `anyOf: ['warlock.vincolo-della-lama', 'guerriero.arma-prescelta']`.
 *   Forward-ref simmetrico ai 3 cross-ref del Guerriero che già citano `warlock.vincolo-della-lama`
 *   in `prerequisites.anyOf` (Cimelio, Arma Migliorata, Fabbro).
 *
 * - **FI-8 stub description-only** (D6): *Braccio Destro* (gregario L2 esplicito, manuale
 *   r. 6001), *Servitore Competente* (req Braccio Destro), *Sosia* (req Braccio Destro).
 *   Refactor unificato Ranghi 1-3 nella PR FI-8 post-12 classi. Le 3 mosse hanno
 *   `prerequisites` strutturato per facilitare il refactor (chain Braccio Destro → Servitore
 *   Competente / Sosia).
 *
 * - **Mossa multiclasse** (FI-17): *Le Vie del Patrono...* sceglie da Paladino/Ladro/Bardo.
 *   Estensione cross-archetipo runtime (Elfo: anche Mago; Mezz'orco: anche Guerriero).
 *   Description-only.
 *
 * - **Catene di prerequisiti**: *Tomo del Sapere* req *Vincolo del Tomo*; *Plagiare l'Anima*
 *   req *Vincolo del Tomo*; *Setta* req *Vincolo della Catena*; *Altri Vincoli* (concede 2°
 *   Vincolo runtime, description-only); *Completamente Vincolato* req *Altri Vincoli*;
 *   *Deflagrazione Potente* req *Deflagrazione Occulta*.
 */

// Helper per i prereq cross-class strutturati. Ritorniamo un nuovo oggetto
// a ogni invocazione (non `as const`) per evitare il problema readonly→mutable
// di Zod su `PrerequisiteSchema`.
function contrattoOrOrdineSovrannaturale(): { anyOf: string[] } {
  return { anyOf: ['warlock.contratto', 'paladino.ordine-sovrannaturale'] };
}

function vincoloLamaOrArmaPrescelta(): { anyOf: string[] } {
  return { anyOf: ['warlock.vincolo-della-lama', 'guerriero.arma-prescelta'] };
}

const neSoUnaPiuDelDiavolo: Move = {
  id: 'warlock.ne-so-una-piu-del-diavolo',
  name: 'Ne so Una più del Diavolo',
  type: 'advanced_2_5',
  classId: 'warlock',
  trigger:
    '***Quando declami conoscenze*** **riguardo qualcosa che interessa anche al tuo patrono**',
  description:
    'Prendi +1 al prossimo tiro che effettui agendo sulla base delle informazioni ottenute.',
  tags: [],
  prerequisites: [contrattoOrOrdineSovrannaturale()],
  replaces: [],
};

const braccioDestro: Move = {
  id: 'warlock.braccio-destro',
  name: 'Braccio Destro',
  type: 'advanced_2_5',
  classId: 'warlock',
  trigger: 'Quando prendi questa mossa al level-up (configurazione del servitore)',
  description:
    'Il tuo *Contratto* o la tua devozione ti conferiscono potere su di un emissario del tuo patrono. Configura il servitore scegliendo nome, descrizione, modificatore +2, Abilità primaria a Rango 1 e una capacità utile concordata con il GM.\n\nSe il servitore dovesse morire o, per qualsiasi motivo, non essere più disponibile, tornerà al tuo fianco al prossimo tramonto, vivo e pronto per nuove avventure.',
  tags: [],
  prerequisites: [contrattoOrOrdineSovrannaturale()],
  replaces: [],
  // D8b — FI-8e: mossa-source di Follower (Braccio Destro hireling).
  followerTemplateId: 'warlock-braccio-destro',
  choiceGroups: [
    {
      id: 'modificatore',
      label: 'Modificatore a +2',
      description: 'Scegli la Caratteristica che ha a +2 (le altre restano +1).',
      pickCount: 1,
      options: [
        { id: 'forza', label: 'Forza' },
        { id: 'destrezza', label: 'Destrezza' },
        { id: 'costituzione', label: 'Costituzione' },
        { id: 'intelligenza', label: 'Intelligenza' },
        { id: 'saggezza', label: 'Saggezza' },
        { id: 'carisma', label: 'Carisma' },
      ],
    },
  ],
  narrativeFields: [
    {
      id: 'nome-emissario',
      label: "Nome dell'emissario",
      placeholder: 'es. Vurgath il Sussurrante, Lady Sapientia, Erebos…',
      required: true,
      multiline: false,
    },
    {
      id: 'descrizione-emissario',
      label: "Descrizione dell'emissario",
      placeholder:
        'Aspetto, natura (demoniaca/fatata/celestiale), comportamento, segni di appartenenza al patrono…',
      required: false,
      multiline: true,
    },
    {
      id: 'abilita-primaria',
      label: 'Abilità primaria (Rango 1)',
      placeholder: "Scegli l'Abilità del servitore.",
      required: true,
      multiline: false,
      oneOf: [
        'adepto',
        'animista',
        'battitore',
        'combattente',
        'guardia-del-corpo',
        'menestrello',
        'sacerdote',
        'scassinatore',
        'scout',
      ],
    },
    {
      id: 'capacita-utile-label',
      label: 'Capacità utile — titolo',
      placeholder: 'es. Visione nelle ombre, Stretta di ferro, Voce del patrono…',
      required: true,
      multiline: false,
    },
    {
      id: 'capacita-utile-desc',
      label: 'Capacità utile — descrizione',
      placeholder: 'Effetto narrativo o meccanico, concordato con il GM.',
      required: false,
      multiline: true,
    },
  ],
};

const servitoreCompetente: Move = {
  id: 'warlock.servitore-competente',
  name: 'Servitore Competente',
  type: 'advanced_2_5',
  classId: 'warlock',
  trigger: 'Quando prendi questa mossa al level-up (upgrade del Braccio Destro)',
  description:
    "Aumenta a Rango 2 l'Abilità primaria del tuo servitore. Inoltre, scegli due Abilità secondarie a Rango 1, oppure una a Rango 1 e una nuova capacità utile da concordare con il GM.",
  tags: [],
  prerequisites: ['warlock.braccio-destro'],
  replaces: [],
  // D8c — FI-41: upgrade with choice del Braccio Destro Warlock.
  // Il template `warlock-braccio-destro` dichiara l'upgrade in `upgrades[]`
  // con `primarySkillRankOverride: 2` + `peculiaritiesFromChoiceGroup`
  // che mappa le 2 opzioni qui dichiarate.
  choiceGroups: [
    {
      id: 'bonus',
      label: 'Bonus aggiuntivo al servitore (oltre primaria a R2)',
      description:
        "Scegli come configurare l'upgrade del servitore (in aggiunta alla primaria a Rango 2, sempre concessa).",
      pickCount: 1,
      options: [
        {
          id: 'due-sec-r1',
          label: 'Due Abilità secondarie a Rango 1',
        },
        {
          id: 'sec-r1-piu-cap',
          label: "Un'Abilità secondaria a Rango 1 + una nuova capacità utile",
        },
      ],
    },
  ],
  narrativeFields: [
    {
      id: 'sc-quale-sec1',
      label: '1ª Abilità secondaria (Rango 1)',
      placeholder: "Scegli l'Abilità.",
      required: true,
      multiline: false,
      oneOf: [
        'adepto',
        'animista',
        'battitore',
        'combattente',
        'guardia-del-corpo',
        'menestrello',
        'sacerdote',
        'scassinatore',
        'scout',
      ],
      requiresChoice: { groupId: 'bonus', optionId: 'due-sec-r1' },
    },
    {
      id: 'sc-quale-sec2',
      label: '2ª Abilità secondaria (Rango 1)',
      placeholder: "Scegli l'Abilità.",
      required: true,
      multiline: false,
      oneOf: [
        'adepto',
        'animista',
        'battitore',
        'combattente',
        'guardia-del-corpo',
        'menestrello',
        'sacerdote',
        'scassinatore',
        'scout',
      ],
      requiresChoice: { groupId: 'bonus', optionId: 'due-sec-r1' },
    },
    {
      id: 'sc-quale-sec',
      label: 'Abilità secondaria (Rango 1)',
      placeholder: "Scegli l'Abilità.",
      required: true,
      multiline: false,
      oneOf: [
        'adepto',
        'animista',
        'battitore',
        'combattente',
        'guardia-del-corpo',
        'menestrello',
        'sacerdote',
        'scassinatore',
        'scout',
      ],
      requiresChoice: { groupId: 'bonus', optionId: 'sec-r1-piu-cap' },
    },
    {
      id: 'sc-cap-label',
      label: 'Nuova capacità utile — titolo',
      placeholder: 'es. Visione nel buio, Stretta di ferro, Voce del patrono…',
      required: true,
      multiline: false,
      requiresChoice: { groupId: 'bonus', optionId: 'sec-r1-piu-cap' },
    },
    {
      id: 'sc-cap-desc',
      label: 'Nuova capacità utile — descrizione',
      placeholder: 'Effetto narrativo o meccanico, concordato con il GM.',
      required: false,
      multiline: true,
      requiresChoice: { groupId: 'bonus', optionId: 'sec-r1-piu-cap' },
    },
  ],
};

// FI-8 stub: il servitore è description-only finché il sistema Seguaci a
// Ranghi 1-3 non sarà strutturato (PR FI-8 post-12 classi).
const sosia: Move = {
  id: 'warlock.sosia',
  name: 'Sosia',
  type: 'advanced_2_5',
  classId: 'warlock',
  trigger: 'Quando comandi al tuo servitore di assumere le tue sembianze',
  description:
    "Tira+**CAR**. Con 10+, scegli 2. Con 7-9, invece, scegli solo 1:\n\n- anche le movenze del tuo sosia sono credibili;\n- anche la voce del tuo sosia è uguale alla tua;\n- l'effetto durerà a lungo.\n\nCon 6-, il tuo servitore morirà per lo stress della trasformazione e tu assumerai le sue sembianze fino al prossimo tramonto.",
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Scegli 2 opzioni dalla lista.' },
    on7to9: { text: 'Scegli 1 opzione dalla lista.' },
    on6minus: {
      text: 'Il servitore muore per lo stress; tu assumi le sue sembianze fino al prossimo tramonto.',
    },
  },
  tags: [],
  prerequisites: ['warlock.braccio-destro'],
  replaces: [],
};

const deflagrazionePotente: Move = {
  id: 'warlock.deflagrazione-potente',
  name: 'Deflagrazione Potente',
  type: 'advanced_2_5',
  classId: 'warlock',
  trigger: 'Sempre (effetto continuo su *Deflagrazione Occulta*)',
  description:
    "Aggiungi le seguenti etichette alla lista di *Deflagrazione Occulta*: *media*, *due bersagli* (-2 danni) e +1d4 danni. L'etichetta 1 *perforante* è sostituita da 2 *perforante*. Inoltre, le etichette di gittata non contano nel limite delle due *diverse* che puoi scegliere.",
  tags: [],
  prerequisites: ['warlock.deflagrazione-occulta'],
  replaces: [],
};

const lAnimaDellAcciaio: Move = {
  id: 'warlock.l-anima-dell-acciaio',
  name: "L'Anima dell'Acciaio",
  type: 'advanced_2_5',
  classId: 'warlock',
  trigger: 'Quando usi la Tua Arma per fare *taglia e spacca*',
  description:
    'Sei entrato in completa sintonia con la Tua Arma: puoi tirare+**CAR** invece che +**FOR** o +**DES**.',
  tags: [],
  prerequisites: [vincoloLamaOrArmaPrescelta()],
  replaces: [],
};

const lamaUltraterrena: Move = {
  id: 'warlock.lama-ultraterrena',
  name: 'Lama Ultraterrena',
  type: 'advanced_2_5',
  classId: 'warlock',
  trigger:
    'Quando desideri che la tua arma appaia nella tua mano o che scompaia in uno spazio extradimensionale',
  description:
    "La Tua Arma esiste contemporaneamente in questo e in un altro mondo. Quando la richiami o la fai scomparire, essa lo farà. Inoltre, l'arma sarà anche in grado di ignorare l'immunità ai danni di tipo fisico (ma non l'Armatura) di qualunque bersaglio colpisca.",
  tags: [],
  prerequisites: [vincoloLamaOrArmaPrescelta()],
  replaces: [],
};

const tomoDelSapere: Move = {
  id: 'warlock.tomo-del-sapere',
  name: 'Tomo del Sapere',
  type: 'advanced_2_5',
  classId: 'warlock',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Il patrono imprime nuove formule nel tuo Tomo. Aggiungi a quelli che puoi lanciare due incantesimi di 1° e due di 3° livello dalla lista del Warlock.',
  tags: [],
  prerequisites: ['warlock.vincolo-del-tomo'],
  replaces: [],
  // FI-33 (D2): manuale V1 r. 6044. Due grant `on-take` separate (una per
  // livello fisso) → la UI mostra 2 sezioni indipendenti, ciascuna con 2
  // spell da scegliere dalla lista Warlock.
  grantsSpells: [
    {
      trigger: 'on-take',
      count: 2,
      levelRule: { kind: 'fixed', level: 1 },
      source: { kind: 'own-class' },
    },
    {
      trigger: 'on-take',
      count: 2,
      levelRule: { kind: 'fixed', level: 3 },
      source: { kind: 'own-class' },
    },
  ],
};

const plagiareLAnima: Move = {
  id: 'warlock.plagiare-l-anima',
  name: "Plagiare l'Anima",
  type: 'advanced_2_5',
  classId: 'warlock',
  trigger:
    'Quando vuoi usare una mossa iniziale o di livello 2-5 di un altro PG con cui hai almeno un Legame attivo',
  description:
    "Spendi 1 presa di *Mana*. La conoscenza è quella del PG da cui prendi in prestito la mossa (livello di classe, soddisfacimento dei prerequisiti, incantesimi conosciuti, ecc.), ma le risorse (prese, PF, ecc.) sono le tue. Se la mossa che vuoi emulare richiede un tiro, usa la Caratteristica da essa indicata. In caso di fallimento, di successo parziale o di altri costi che comportino una penalità all'uso della mossa plagiata o la sua inibizione temporanea, applicala a *Lanciare Incantesimi del Patrono*. Usa la mossa che stai plagiando così com'è, senza potenziamenti dovuti alle altre mosse del PG da cui l'hai presa in prestito. Tuttavia, se qualche *tua* mossa la modifica, applica pure il modificatore. Gli effetti passivi delle mosse che usi in questo modo hanno una durata limitata: se, ad esempio, la mossa che prendi in prestito ti da 2 Armatura, l'effetto durerà solo per quel combattimento o per il tempo necessario a risolvere la situazione.",
  tags: [],
  prerequisites: ['warlock.vincolo-del-tomo'],
  replaces: [],
};

const setta: Move = {
  id: 'warlock.setta',
  name: 'Setta',
  type: 'advanced_2_5',
  classId: 'warlock',
  trigger:
    'Quando ti dedichi a rafforzare la tua Setta (investendoci risorse, reclutando validi seguaci, o espandendo la sua influenza)',
  description:
    "Hai creato la tua *Setta* personale! Tira+**CAR**. Con 10+, ottieni 1 presa. Con 7-9, lo stesso, ma la *Setta* attira attenzioni indesiderate o il tuo patrono pretende un tributo per la sua crescita. Con 6-, non guadagni prese, e le attività della *Setta* danno luogo a un problema che richiede la tua immediata attenzione.\n\nPuoi spendere prese per fare in modo che la tua *Setta* porti a compimento con successo un piano da te ideato. Il costo dipende dalla complessità:\n\n- semplice, ad esempio recuperare informazioni specifiche o un oggetto non strettamente sorvegliato, diffondere voci e dicerie, o compiere qualche piccola azione di disturbo: 2 prese;\n- articolato, ad esempio recuperare informazioni poco disponibili o un oggetto raro, influenzare l'opinione pubblica di un piccolo insediamento o di un quartiere cittadino, corrompere un singolo politico, oppure compiere un sabotaggio in piena regola o l'omicidio di qualcuno non troppo in vista: 4 prese;\n- complesso, ad esempio trafugare un oggetto ben custodito o venire a conoscenza di un segreto, corrompere una fazione politica, incitare alla rivolta, oppure destabilizzare un'organizzazione, magari rovesciando un leader minore (con la forza o meno): 6 prese.\n\nSe qualcuno indaga su ciò che è successo, puoi spendere 1 presa e fare in modo che non sia possibile risalire a te (ma qualche altro membro della *Setta* potrebbe venire coinvolto). Al posto di una presa di *Setta* puoi anche riscuotere un favore del *Vincolo della Catena* da un PNG che possa essere utile alla riuscita del piano.",
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Ottieni 1 presa di *Setta*.' },
    on7to9: {
      text: '1 presa, ma attiri attenzioni indesiderate o il patrono pretende un tributo.',
    },
    on6minus: { text: 'Niente prese; la *Setta* dà luogo a un problema urgente.' },
  },
  tags: [],
  prerequisites: ['warlock.vincolo-della-catena'],
  replaces: [],
};

const altriVincoli: Move = {
  id: 'warlock.altri-vincoli',
  name: 'Altri Vincoli',
  type: 'advanced_2_5',
  classId: 'warlock',
  trigger: 'Quando prendi questa mossa al level-up',
  description: 'Ottieni un Vincolo del Warlock che non hai già.',
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-31 (D5): concede un Vincolo residuo del gruppo del Warlock
  // (Catena / Tomo / Lama — 1 scelto al L1, 2 residui).
  grantsMoveChoice: { kind: 'from-group', groupId: 'vincolo-del-patrono' },
};

const completamenteVincolato: Move = {
  id: 'warlock.completamente-vincolato',
  name: 'Completamente Vincolato',
  type: 'advanced_2_5',
  classId: 'warlock',
  trigger: 'Quando prendi questa mossa al level-up',
  description: "Ottieni anche l'ultimo Vincolo del Warlock che ti mancava.",
  tags: [],
  prerequisites: ['warlock.altri-vincoli'],
  replaces: [],
  // FI-31 (D5): catena 2-step dopo *Altri Vincoli* — pool=1 residuo
  // (terzo Vincolo del Warlock).
  grantsMoveChoice: { kind: 'from-group', groupId: 'vincolo-del-patrono' },
};

const punizioneRimandata: Move = {
  id: 'warlock.punizione-rimandata',
  name: 'Punizione Rimandata',
  type: 'advanced_2_5',
  classId: 'warlock',
  trigger: 'Quando subisci un ammontare di danni che ti ucciderebbe',
  description:
    'Puoi, invece, prendere -1 continuato a tutti i tiri e negare tale danno. **Non puoi usare questa mossa se hai già il malus**, ma puoi rimuoverlo in qualsiasi momento accettando di subire il numero di danni che hai evitato in precedenza.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const laLezioneDelDolore: Move = {
  id: 'warlock.la-lezione-del-dolore',
  name: 'La Lezione del Dolore',
  type: 'advanced_2_5',
  classId: 'warlock',
  trigger: 'Quando non sei a pieni PF',
  description: 'Prendi +1 Armatura.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const ilPrezzoDellAnima: Move = {
  id: 'warlock.il-prezzo-dell-anima',
  name: "Il Prezzo dell'Anima",
  type: 'advanced_2_5',
  classId: 'warlock',
  trigger: 'Quando passi del tempo a prendere confidenza con qualcuno',
  description:
    'Tira+**SAG**. Con 10+, il tuo patrono ti sussurra una cosa che quella persona desidera veramente. Con 7-9, lo stesso, ma lei percepisce qualcosa della tua vera natura. Puoi usare quel che hai scoperto come leva per *parlamentare*.',
  roll: {
    stat: 'saggezza',
    on10plus: { text: 'Il patrono ti sussurra cosa la persona desidera veramente.' },
    on7to9: { text: 'Lo stesso, ma lei percepisce qualcosa della tua vera natura.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const leVieDelPatrono: Move = {
  id: 'warlock.le-vie-del-patrono',
  name: 'Le Vie del Patrono...',
  type: 'advanced_2_5',
  classId: 'warlock',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    "Scegli una mossa non multiclasse dalla lista del Paladino, del Ladro o del Bardo. Se hai l'archetipo Elfo, puoi pescare anche dal Mago. Se hai l'archetipo Mezz'orco, puoi pescare anche dal Guerriero.",
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool base Paladino/Ladro/Bardo + Elfo→Mago + Mezz'orco→Guerriero
  // (manuale rr. 5838, 5840).
  multiclass: {
    picks: { kind: 'fixed-classes', classes: ['paladino', 'ladro', 'bardo'] },
    extendsByArchetype: [
      { archetypeId: 'elfo', extraClasses: ['mago'] },
      { archetypeId: 'mezzorco', extraClasses: ['guerriero'] },
    ],
  },
};

export const warlockAdvanced2to5Moves: readonly Move[] = [
  neSoUnaPiuDelDiavolo,
  braccioDestro,
  servitoreCompetente,
  sosia,
  deflagrazionePotente,
  lAnimaDellAcciaio,
  lamaUltraterrena,
  tomoDelSapere,
  plagiareLAnima,
  setta,
  altriVincoli,
  completamenteVincolato,
  punizioneRimandata,
  laLezioneDelDolore,
  ilPrezzoDellAnima,
  leVieDelPatrono,
] as const;
